import { useState, useEffect } from 'react';

const CHANGELOGS_API_URL = 'https://visualgrid.app/api/changelogs';
const DOWNLOADS_URL = 'https://visualgrid.app/downloads/';
const CHECK_INTERVAL_MS = 60 * 60 * 1000; // 1 heure
const DISMISSED_KEY = 'visualgrid-update-dismissed';

export interface UpdateInfo {
  currentVersion: string;
  latestVersion: string;
  downloadsUrl: string;
}

function parseVersion(v: string): number[] {
  return v.replace(/^v/, '').split('.').map(Number);
}

function isNewerVersion(latest: string, current: string): boolean {
  const l = parseVersion(latest);
  const c = parseVersion(current);
  for (let i = 0; i < Math.max(l.length, c.length); i++) {
    const lv = l[i] ?? 0;
    const cv = c[i] ?? 0;
    if (lv > cv) return true;
    if (lv < cv) return false;
  }
  return false;
}

function isTauriEnvironment(): boolean {
  if (typeof window === 'undefined') return false;
  if ((window as any).__TAURI_IPC__ !== undefined) return true;
  if ((window as any).__TAURI__) return true;
  if (typeof navigator !== 'undefined' && navigator.userAgent.includes('Tauri')) return true;
  return false;
}

async function getCurrentTauriVersion(): Promise<string> {
  try {
    const { getVersion } = await import('@tauri-apps/api/app');
    return await getVersion();
  } catch {
    return '0.0.0';
  }
}

async function fetchLatestVersion(): Promise<string | null> {
  try {
    const res = await fetch(CHANGELOGS_API_URL, {
      cache: 'no-store',
      signal: AbortSignal.timeout(8000),
    });
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0 && data[0].version) {
        return data[0].version as string;
      }
    }
  } catch {
    // réseau indisponible ou timeout — silencieux
  }
  return null;
}

export function useUpdateCheck() {
  const [updateInfo, setUpdateInfo] = useState<UpdateInfo | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isTauriEnvironment()) return;

    const dismissedVersion = localStorage.getItem(DISMISSED_KEY);

    const check = async () => {
      const [currentVersion, latestVersion] = await Promise.all([
        getCurrentTauriVersion(),
        fetchLatestVersion(),
      ]);

      if (!latestVersion) return;
      if (dismissedVersion === latestVersion) return;
      if (isNewerVersion(latestVersion, currentVersion)) {
        setUpdateInfo({ currentVersion, latestVersion, downloadsUrl: DOWNLOADS_URL });
      }
    };

    check();
    const interval = setInterval(check, CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const dismiss = () => {
    if (updateInfo) {
      localStorage.setItem(DISMISSED_KEY, updateInfo.latestVersion);
    }
    setDismissed(true);
    setUpdateInfo(null);
  };

  return { updateInfo: dismissed ? null : updateInfo, dismiss };
}
